<template>
  <div class="mt" data-test="pfe-tab-map-body">
    <div v-if="formsError" class="mt-alert mt-alert--error" role="alert" data-test="pfe-forms-error">
      <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
      <span>Couldn&rsquo;t load your Teamio Forms list &mdash; {{ formsError }}</span>
    </div>

    <div v-if="rows.length === 0" class="mt-empty" data-test="pfe-empty">
      <div class="mt-empty-icon"><i class="fa fa-file-text-o" aria-hidden="true"></i></div>
      <div class="mt-empty-title">No procedures matched yet</div>
      <p class="mt-empty-body">
        Each row matches a few procedure codes to the consent form patients sign before the visit, and the after-care
        instructions we send when Open Dental marks the procedure complete.
      </p>
      <button type="button" class="mt-btn mt-btn--primary" data-test="pfe-add-row" @click="$emit('add')">
        <i class="fa fa-plus" aria-hidden="true"></i> Add row
      </button>
    </div>

    <template v-else>
      <div class="mt-summary" data-test="pfe-summary">
        <strong>{{ summary.groups }}</strong>
        <span class="mt-dot">&middot;</span>
        <span>{{ summary.consent }}</span>
        <span class="mt-dot">&middot;</span>
        <span>{{ summary.postOp }}</span>
        <template v-if="summary.hasReview">
          <span class="mt-dot">&middot;</span>
          <span class="mt-review" data-test="pfe-summary-review"><span class="mt-review-dot"></span>{{ summary.review }}</span>
        </template>
        <span class="mt-hint">Pre-filled from your Open Dental procedures. Check the highlighted cells, then save.</span>
      </div>

      <div v-if="analysis.overlapRows.length" class="mt-alert mt-alert--warn" role="status" data-test="pfe-banner-double-send">
        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
        <div><strong>{{ analysis.overlapHead }}</strong> {{ analysis.overlapBody }}</div>
      </div>

      <div class="mt-card" data-test="pfe-table">
        <div class="mt-head" aria-hidden="true">
          <span>Group &amp; procedure codes</span>
          <span>Consent form <span class="mt-opt">&middot; optional</span></span>
          <span>Post-op form <span class="mt-opt">&middot; optional</span></span>
          <span>Auto-send</span>
          <span></span>
        </div>

        <mapping-row
          v-for="(row, index) in rows"
          :key="row._key"
          :row="row"
          :forms="forms"
          :forms-loading="formsLoading"
          :code-options="codeOptions"
          :codes-loading="codesLoading"
          :workflows="workflowsFor(row)"
          :auto-open-codes="row._key === openCodesKey"
          @change="$emit('change', index, $event)"
          @remove="$emit('remove', index)"
        />

        <div class="mt-foot">
          <button type="button" class="mt-btn mt-btn--ghost" data-test="pfe-add-row" @click="$emit('add')">
            <i class="fa fa-plus" aria-hidden="true"></i> Add row
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import MappingRow from './MappingRow.vue';
import { summaryParts } from '@/utils/postOp/rows';

/**
 * The "Procedure mappings" tab: summary line, double-send banner, and the table. Presentational — the editor owns the rows
 * and the analysis (utils/postOp/rows.js analyzeRows) and passes both in.
 */
export default {
  name: 'PfeMappingsTab',
  components: { MappingRow },
  props: {
    rows: { type: Array, default: () => [] },
    forms: { type: Array, default: () => [] },
    formsLoading: { type: Boolean, default: false },
    formsError: { type: String, default: '' },
    codeOptions: { type: Array, default: () => [] },
    codesLoading: { type: Boolean, default: false },
    analysis: { type: Object, required: true },
    /** `_key` of the row "+ Add row" just created: its code menu opens on mount. */
    openCodesKey: { type: [Number, String], default: null },
  },
  computed: {
    summary() { return summaryParts(this.analysis); },
  },
  methods: {
    workflowsFor(row) {
      const hit = this.analysis.overlapRows.find((o) => o.key === row._key);
      return hit ? hit.workflows : [];
    },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.mt {
  max-width: 1320px;
  margin: 0 auto;
  padding: 22px 32px 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mt-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 14px;
  color: $pfe-neutral-600;

  strong { color: $pp-text; font-weight: 700; }
}
.mt-dot { color: $pp-border-strong; }
.mt-review {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: $pfe-amber-ink;
}
.mt-review-dot { width: 8px; height: 8px; border-radius: 50%; background: $pp-amber; }
.mt-hint { margin-left: auto; font-size: 13px; color: $pfe-neutral-500; }

.mt-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  color: $pfe-neutral-800;

  &--warn { background: $pp-amber-subtle; border: 1px solid #f3cf8e; i { color: #b77400; margin-top: 3px; } }
  &--error { background: $pp-red-subtle; border: 1px solid $pp-red-border; color: $pp-red-text; i { margin-top: 3px; } }
}

.mt-card {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
}
// Docked while the rows scroll (Nilay 2026-10-17), so nobody has to scroll back up to see which column is which. It sticks to
// the top of .pfe-scroll (the editor's scroll container; nothing between it and here clips) for as long as the card is on
// screen. z-index 20: above the rows scrolling under it, BELOW PopMenu (30) so an open code/form menu is never covered. The
// 1px shadow is the line under the header once it's docked; at rest it sits on the first row's own border-top.
.mt-head {
  position: sticky;
  top: 0;
  z-index: 20;
  box-shadow: 0 1px 0 $pp-border;
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) minmax(0, 1.25fr) minmax(0, 1.25fr) 104px 36px;
  gap: 16px;
  padding: 12px 20px;
  background: $pfe-neutral-25;
  border-radius: $pp-radius $pp-radius 0 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $pfe-neutral-500;
}
.mt-opt { text-transform: none; letter-spacing: 0; font-weight: 500; color: $pfe-neutral-400; }
.mt-foot { padding: 12px 14px; border-top: 1px solid $pp-border; }

.mt-empty {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: $pp-radius;
  box-shadow: $pp-shadow;
  padding: 56px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}
.mt-empty-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: $pfe-neutral-50;
  color: $pfe-neutral-500;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.mt-empty-title { font-family: $pp-font-display; font-weight: 800; font-size: 20px; letter-spacing: -0.01em; }
.mt-empty-body { margin: 0; max-width: 480px; font-size: 14.5px; line-height: 1.55; color: $pfe-neutral-600; }

.mt-btn {
  &--primary { @include pfe-button(primary); }
  &--ghost { @include pfe-button(ghost); }
}

@media (max-width: 900px) {
  .mt { padding: 18px 16px 32px; }
  .mt-head { display: none; }
  .mt-hint { margin-left: 0; flex-basis: 100%; }
}
</style>
